import { scopeStateSchema } from '@shared/models/scope-selection.model';
import { activityTypes } from '@shared/models/design/activities/activity-type.model';
import type { ProjectMetadata } from '@shared/models/project.model';
import type { Results, SbefGroupResults } from '@shared/models/unit.model';
import {
  getFlatResults,
  SBEF_GROUP_BREAKDOWN_KEYS,
} from '@shared/helpers/sbef-results.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { isConversionFactorUnit } from '@shared/helpers/unit.helpers';
import {
  GenericValidationError,
  type ValidationResult,
} from './validation.models';

/** Prefix aligned with `ProjectValidationErrors.INVALID_METADATA`. */
const INVALID_METADATA = 'Invalid metadata';

const ACTIVITY_TYPE_SET = new Set<string>(activityTypes);

/**
 * True when {@link value} is a finite number (rejects NaN / ±Infinity).
 */
const isFiniteNumber = (value: unknown): value is number =>
  guards.is.number(value) && Number.isFinite(value);

/**
 * Validate {@link ProjectMetadata.design.gfa}.
 */
const isValidTrimmedDesign = (design: unknown): ValidationResult => {
  if (!guards.is.object(design)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: design must be an object`,
    );
  }
  if (!isFiniteNumber(design.gfa) || design.gfa < 0) {
    return new GenericValidationError(
      `${INVALID_METADATA}: design.gfa must be a finite number ≥ 0`,
    );
  }
  return true;
};

/**
 * Validate optional {@link ProjectMetadata.scope_targets}.
 */
const isValidScopeTargets = (scopeTargets: unknown): ValidationResult => {
  if (!guards.is.object(scopeTargets)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: scope_targets must be an object`,
    );
  }
  for (const [key, value] of Object.entries(scopeTargets)) {
    if (!guards.is.string(key) || key.length === 0) {
      return new GenericValidationError(
        `${INVALID_METADATA}: scope_targets keys must be non-empty strings`,
      );
    }
    if (!isFiniteNumber(value)) {
      return new GenericValidationError(
        `${INVALID_METADATA}: scope_targets.${key} must be a finite number`,
      );
    }
  }
  return true;
};

/**
 * Validate a denormalized {@link ProjectMapPreview} stored on metadata.
 */
const isValidMapPreview = (preview: unknown): ValidationResult => {
  if (!guards.is.object(preview)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: map_preview must be an object`,
    );
  }
  const location = preview.location;
  if (
    !guards.is.object(location) ||
    !isFiniteNumber(location.lat) ||
    !isFiniteNumber(location.lng)
  ) {
    return new GenericValidationError(
      `${INVALID_METADATA}: map_preview.location must have finite lat/lng`,
    );
  }

  const outlines = preview.outlines;
  if (!guards.is.object(outlines) || outlines.type !== 'FeatureCollection') {
    return new GenericValidationError(
      `${INVALID_METADATA}: map_preview.outlines must be a FeatureCollection`,
    );
  }
  if (!guards.is.array(outlines.features)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: map_preview.outlines.features must be an array`,
    );
  }

  for (const feature of outlines.features) {
    if (!guards.is.object(feature) || feature.type !== 'Feature') {
      return new GenericValidationError(
        `${INVALID_METADATA}: map_preview feature must be a GeoJSON Feature`,
      );
    }
    const properties = feature.properties;
    if (
      !guards.is.object(properties) ||
      !guards.is.string(properties.buildingId) ||
      properties.buildingId.length === 0
    ) {
      return new GenericValidationError(
        `${INVALID_METADATA}: map_preview feature.properties.buildingId is required`,
      );
    }
    const geometry = feature.geometry;
    if (
      !guards.is.object(geometry) ||
      geometry.type !== 'MultiPolygon' ||
      !guards.is.array(geometry.coordinates)
    ) {
      return new GenericValidationError(
        `${INVALID_METADATA}: map_preview feature.geometry must be a MultiPolygon`,
      );
    }
  }
  return true;
};

/**
 * Validate denormalized {@link ProgramGfaArea} rows on metadata.
 */
const isValidProgramAreas = (areas: unknown): ValidationResult => {
  if (!guards.is.array(areas)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: program_areas must be an array`,
    );
  }
  for (const area of areas) {
    if (!guards.is.object(area)) {
      return new GenericValidationError(
        `${INVALID_METADATA}: program_areas entries must be objects`,
      );
    }
    if (!guards.is.string(area.type) || !ACTIVITY_TYPE_SET.has(area.type)) {
      return new GenericValidationError(
        `${INVALID_METADATA}: program_areas.type must be an ActivityType`,
      );
    }
    if (!isFiniteNumber(area.area) || area.area < 0) {
      return new GenericValidationError(
        `${INVALID_METADATA}: program_areas.area must be a finite number ≥ 0`,
      );
    }
  }
  return true;
};

/**
 * Structure-only check for denormalized list rollups.
 * Allows zero totals — conversion-factor positivity does not apply to stored results.
 */
const isValidListResultValues = (results: Results): ValidationResult => {
  for (const [unit, value] of Object.entries(results)) {
    if (!isConversionFactorUnit(unit)) {
      return new GenericValidationError(
        `${INVALID_METADATA}: unit "${unit}" is not valid in list results`,
      );
    }
    if (!guards.is.number(value) || !Number.isFinite(value)) {
      return new GenericValidationError(
        `${INVALID_METADATA}: list result values must be finite numbers`,
      );
    }
  }
  return true;
};

/**
 * Validate list result rollups that may include inline SBEF group buckets.
 */
const isValidListResults = (
  results: unknown,
  field: 'overview_results' | 'baseline_results',
): ValidationResult => {
  if (!guards.is.object(results)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: ${field} must be an object`,
    );
  }

  const flatOk = isValidListResultValues(
    getFlatResults(results as SbefGroupResults),
  );
  if (flatOk !== true) {
    return new GenericValidationError(
      `${INVALID_METADATA}: ${field} flat totals are invalid (${flatOk.message})`,
    );
  }

  for (const [key, value] of Object.entries(results)) {
    if (!SBEF_GROUP_BREAKDOWN_KEYS.has(key)) continue;
    if (!guards.is.object(value)) {
      return new GenericValidationError(
        `${INVALID_METADATA}: ${field}.${key} must be a results object`,
      );
    }
    const bucketOk = isValidListResultValues(value as Results);
    if (bucketOk !== true) {
      return new GenericValidationError(
        `${INVALID_METADATA}: ${field}.${key} is invalid (${bucketOk.message})`,
      );
    }
  }
  return true;
};

/**
 * Validate {@link ProjectMetadata} used on {@link Project.meta} and list API `metadata`.
 * Unknown legacy keys are ignored so older rows can still load until strip migrations run.
 */
export const isValidProjectMetadata = (
  meta: ProjectMetadata | undefined,
): ValidationResult => {
  if (!meta) {
    return new GenericValidationError(`${INVALID_METADATA}: meta not defined`);
  }
  if (!guards.is.object(meta)) {
    return new GenericValidationError(
      `${INVALID_METADATA}: meta must be an object`,
    );
  }

  const designOk = isValidTrimmedDesign(meta.design);
  if (designOk !== true) return designOk;

  if (
    meta.selected_scope !== undefined &&
    !scopeStateSchema.safeParse(meta.selected_scope).success
  ) {
    return new GenericValidationError(
      `${INVALID_METADATA}: invalid selected_scope`,
    );
  }

  if (meta.enabled_scope_ids !== undefined) {
    if (
      !guards.is.array(meta.enabled_scope_ids) ||
      !meta.enabled_scope_ids.every(
        (id) => guards.is.string(id) && id.trim().length > 0,
      )
    ) {
      return new GenericValidationError(
        `${INVALID_METADATA}: enabled_scope_ids must contain non-empty strings`,
      );
    }
  }

  if (meta.scope_targets !== undefined) {
    const scopeOk = isValidScopeTargets(meta.scope_targets);
    if (scopeOk !== true) return scopeOk;
  }

  if (meta.map_preview !== undefined) {
    const previewOk = isValidMapPreview(meta.map_preview);
    if (previewOk !== true) return previewOk;
  }

  if (meta.program_areas !== undefined) {
    const areasOk = isValidProgramAreas(meta.program_areas);
    if (areasOk !== true) return areasOk;
  }

  if (meta.building_count !== undefined) {
    if (!Number.isInteger(meta.building_count) || meta.building_count < 0) {
      return new GenericValidationError(
        `${INVALID_METADATA}: building_count must be an integer ≥ 0`,
      );
    }
  }

  if (meta.overview_results !== undefined) {
    const overviewOk = isValidListResults(
      meta.overview_results,
      'overview_results',
    );
    if (overviewOk !== true) return overviewOk;
  }

  if (meta.baseline_results !== undefined) {
    const baselineOk = isValidListResults(
      meta.baseline_results,
      'baseline_results',
    );
    if (baselineOk !== true) return baselineOk;
  }

  return true;
};
